import type { AlterationDetail, GarmentAlterationInput } from '../api/bookings';

/**
 * The work a tailor is to do on one garment, being typed at the counter (spec Section 11).
 *
 * <p>
 * In the shape `draft.ts`, `discount.ts` and `intake.ts` established: plain data, pure functions
 * over it, nothing that touches the server. That is what lets the tailor's sentence appear under
 * the clerk's hands as they type, and it keeps the awkward parts — a delta of zero, a slipped
 * decimal, a measure nobody named — testable without rendering anything.
 * </p>
 *
 * <p>
 * <strong>These rows were unwritable until now.</strong> `TblRentalOrderItemAlteration` has existed
 * since the Section 8 rebuild, with an entity, an index, a DTO and a projection reading it — and no
 * endpoint, service method or request field able to put a row in it. Since the tailor's docket keys
 * on `alterations.length > 0`, that made the whole print module unreachable in a running shop: the
 * button never rendered. This module is the counter's half of closing that.
 * </p>
 */

/**
 * One row as the editor holds it. Strings, because a half-typed number is not a number yet.
 *
 * <p>
 * <strong>A box takes the finished size</strong> — chest 46, not "let out 1.5" (owner,
 * 2026-10-20): the karigar cuts to the number on the card. A row written before then carries a
 * signed change instead, and stays one (`kind: 'change'`) until somebody types in its box, when it
 * becomes a size like any other. Sent back untouched, it goes back as the change it was.
 * </p>
 */
export interface DraftAlteration {
  /** Client-side only, for React keys. The server assigns nothing — rows are replaced, not patched. */
  key: string;
  /** `Sleeve`, `Waist`, `TrouserHem` — or anything else the shop needs. */
  measure: string;
  /** In inches, as typed: the finished size, or (on an untouched old row) the signed change. */
  figure: string;
  kind: 'size' | 'change';
  note: string;
}

/**
 * The smallest and largest finished size, matching `BookingService.MinSizeInches`/`MaxSizeInches`.
 * Nothing on a body measures under an inch or past eighty; a figure outside is a small change typed
 * into a size box (0.5) or a slipped decimal (460). Inside them 4.6 and 1.5 still pass — one range
 * for every measure cannot catch them, the accepted cost of sizes only (decision log, 2026-10-20).
 */
export const sizeInchesRange = { min: 1, max: 80 } as const;

/**
 * The largest single adjustment, matching `BookingService.MaxAlterationInches`.
 *
 * Generous — a wedding sherwani genuinely comes in four inches — and bounded, because a slipped
 * decimal is a garment cut to pieces.
 */
export const maxAlterationInches = 24;

/** Matches `BookingService.MaxAlterationsPerGarment`. */
export const maxAlterations = 20;

let nextKey = 0;

/** Keys only have to be unique within one drawer's lifetime, which a counter makes cheap. */
export function alterationKey(): string {
  nextKey += 1;

  return `a${nextKey}`;
}

export function emptyAlteration(measure = ''): DraftAlteration {
  return { key: alterationKey(), measure, figure: '', kind: 'size', note: '' };
}

/**
 * The rows a garment already carries, ready to edit.
 *
 * The figure comes back as it was stored rather than reformatted: a clerk who typed `46.5` should
 * see `46.5` when they reopen the card, not `46½` they then have to re-type as a number. An old
 * change keeps its number as stored (a number input refuses a leading plus), and
 * {@link olderChanges} is what tells the clerk it is a change.
 */
export function fromDetails(details: readonly AlterationDetail[]): DraftAlteration[] {
  return details.map((detail) => {
    const change = (detail.sizeInches ?? null) === null ? (detail.deltaInches ?? 0) : null;

    return {
      key: alterationKey(),
      measure: detail.measure,
      figure: String(change ?? detail.sizeInches),
      kind: change === null ? 'size' : 'change',
      note: detail.note ?? '',
    };
  });
}

export function addAlteration(rows: DraftAlteration[], measure = ''): DraftAlteration[] {
  return [...rows, emptyAlteration(measure)];
}

/** A figure typed into a row makes it a finished size, whatever it was before. */
export function updateAlteration(
  rows: DraftAlteration[],
  key: string,
  changes: Partial<Omit<DraftAlteration, 'key' | 'kind'>>,
): DraftAlteration[] {
  return rows.map((row) =>
    row.key === key
      ? { ...row, ...changes, ...(changes.figure === undefined ? {} : { kind: 'size' as const }) }
      : row,
  );
}

export function removeAlteration(rows: DraftAlteration[], key: string): DraftAlteration[] {
  return rows.filter((row) => row.key !== key);
}

/**
 * The card's two halves — what goes on top, and what goes below.
 *
 * <p>
 * <strong>Shaped after the karigar card a shop already fills in by hand</strong>: a row of labelled
 * boxes for the upper garment and another for the lower, tabbed through rather than added one at a
 * time. The old editor asked for a three-field card per measurement plus a click to add it, which
 * is about twenty-one fields and seven clicks for a card somebody fills in twenty seconds.
 * </p>
 *
 * <p>
 * <strong>Only measures the docket has trade wording for, in every language.</strong> This is
 * deliberately not the place to invent vocabulary: `narrativeText.test.ts` requires Gujarati and
 * Hindi verbs for every member of `knownMeasures`, and its own comment gives the reason — a measure
 * with no wording prints as "Sleeve" in the middle of a Gujarati sentence, which a tailor reads as a
 * mistake rather than an instruction. Anything the two halves do not cover goes in the free rows
 * below them, where the renderer prints the measure's own name in any language.
 * </p>
 */
export interface MeasureGroup {
  title: string;
  hint: string;
  measures: readonly string[];
}

/**
 * <strong>The shop's own printed card, box for box and in its order</strong> (owner, 2026-10-17,
 * from a photograph of the card): upper લં. સો. છા. પે. સી. બા. કો., lower લં. ક. સી. જા. જો. મો. ગો.
 * The earlier card (Waist and Neck on top, a trouser hem below) was a guess at a card nobody had
 * shown us; its keys stay known to the docket so measurements already written still print.
 */
export const measureGroups: readonly MeasureGroup[] = [
  {
    title: 'Upper',
    hint: 'Sherwani, kurta, coat',
    measures: ['Length', 'Shoulder', 'Chest', 'Pet', 'Seat', 'Sleeve', 'Collar'],
  },
  {
    title: 'Lower',
    hint: 'Pyjama, trouser',
    measures: ['TrouserLength', 'TrouserWaist', 'TrouserSeat', 'Thigh', 'Joro', 'Moriyu', 'Golai'],
  },
];

/**
 * What each box is called on the screen: the shop's own Gujarati word for it (owner, 2026-10-17),
 * the English beside it, and the short form printed on its paper card. Two halves both have a
 * length and a seat — the key tells them apart, the label need not.
 */
export const measureLabels: Readonly<
  Record<string, { gujarati: string; name: string; short: string }>
> = {
  Length: { gujarati: 'લંબાઈ', name: 'Length', short: 'લં.' },
  Shoulder: { gujarati: 'સોલ્ડર', name: 'Shoulder', short: 'સો.' },
  Chest: { gujarati: 'છાતી', name: 'Chest', short: 'છા.' },
  Pet: { gujarati: 'પેટ', name: 'Pet', short: 'પે.' },
  Seat: { gujarati: 'સીટ', name: 'Seat', short: 'સી.' },
  Sleeve: { gujarati: 'બાઈ', name: 'Sleeve', short: 'બા.' },
  Collar: { gujarati: 'કોલર', name: 'Collar', short: 'કો.' },
  TrouserLength: { gujarati: 'લંબાઈ', name: 'Length', short: 'લં.' },
  TrouserWaist: { gujarati: 'કમર', name: 'Waist', short: 'ક.' },
  TrouserSeat: { gujarati: 'સીટ', name: 'Seat', short: 'સી.' },
  Thigh: { gujarati: 'જાંઘ', name: 'Thigh', short: 'જા.' },
  Joro: { gujarati: 'જોરો', name: 'Joro', short: 'જો.' },
  Moriyu: { gujarati: 'મોરિયું', name: 'Moriyu', short: 'મો.' },
  Golai: { gujarati: 'ગોળાઈ', name: 'Golai', short: 'ગો.' },
};

/** A box's label — "લંબાઈ · Length" — or the measure's own name for one the card does not carry. */
export function measureLabel(measure: string): string {
  const label = measureLabels[measure];

  return label ? `${label.gujarati} · ${label.name}` : measure;
}

/** Every measure the two halves cover, which is what the free rows below them exclude. */
export const groupedMeasures: readonly string[] = measureGroups.flatMap((group) => group.measures);

/**
 * What is typed in one of the card's boxes, or nothing if that box is empty.
 *
 * Reading by measure rather than by key, because a box on the card *is* its measure — there is no
 * row until somebody types in it, and none left once they clear it.
 */
export function measureValue(rows: readonly DraftAlteration[], measure: string): string {
  return rows.find((row) => row.measure === measure)?.figure ?? '';
}

/**
 * Types a figure into one of the card's boxes.
 *
 * <p>
 * <strong>Clearing the box removes the row.</strong> A blank box means "nothing to do here", and
 * leaving a row behind with an empty figure would fail `alterationProblems` for a measurement the
 * clerk has just said they do not want. Typing in a box makes its row a finished size. That is the same statement-versus-edit distinction the
 * discount lines draw: what is on the card is the whole of what is being asked for.
 * </p>
 *
 * <p>
 * A note already typed against that measure survives an edit to the figure, and dies with the row
 * when the figure is cleared — the note describes the adjustment, so it cannot outlive it.
 * </p>
 */
export function setMeasure(
  rows: DraftAlteration[],
  measure: string,
  figure: string,
): DraftAlteration[] {
  if (figure.trim() === '') {
    return rows.filter((row) => row.measure !== measure);
  }

  return rows.some((row) => row.measure === measure)
    ? rows.map((row) => (row.measure === measure ? { ...row, figure, kind: 'size' as const } : row))
    : [...rows, { ...emptyAlteration(measure), figure }];
}

/**
 * The measures still holding a change from before the card took finished sizes — named, so the card
 * can say which boxes read "−1.5" as a change rather than a size. Empty once each is retyped.
 */
export function olderChanges(rows: readonly DraftAlteration[]): string[] {
  return rows
    .filter((row) => row.kind === 'change')
    .map((row) => measureLabels[row.measure]?.name ?? row.measure);
}

/** The rows that are not one of the card's boxes — a safa's, a dupatta's, anything else. */
export function freeRows(rows: readonly DraftAlteration[]): DraftAlteration[] {
  return rows.filter((row) => !groupedMeasures.includes(row.measure));
}

/**
 * A row that is still blank — no measure and no figure.
 *
 * The editor always keeps one of these at the bottom so there is somewhere to type, and it is
 * dropped on the way out rather than being refused: an empty row the clerk never touched is not a
 * mistake, it is the row they did not need.
 */
function isBlank(row: DraftAlteration): boolean {
  return row.measure.trim() === '' && row.figure.trim() === '' && row.note.trim() === '';
}

/**
 * What stands between these rows and a docket, mirroring the server's own refusals.
 *
 * <p>
 * Stated here so the clerk finds out while the field is under their cursor rather than after a 400
 * — the cost `garmentMoves.ts` names about itself applies: a rule added in C# and not here is one
 * the screen quietly stops enforcing. The server checks again regardless.
 * </p>
 */
export function alterationProblems(rows: readonly DraftAlteration[]): string[] {
  const problems: string[] = [];
  const real = rows.filter((row) => !isBlank(row));

  if (real.length > maxAlterations) {
    problems.push(`A garment may carry ${maxAlterations} alterations at once.`);
  }

  for (const row of real) {
    const measure = row.measure.trim();
    const figure = Number(row.figure);

    if (measure === '') {
      problems.push('Every measurement needs to say what it measures.');
      continue;
    }

    if (row.kind === 'size') {
      if (row.figure.trim() === '' || !Number.isFinite(figure)) {
        problems.push(`Give ${measure} its finished size in inches.`);
      } else if (figure < sizeInchesRange.min || figure > sizeInchesRange.max) {
        problems.push(
          `${measure} is ${figure} inches. A finished size is between ${sizeInchesRange.min} and ${sizeInchesRange.max} inches — check the figure.`,
        );
      }

      continue;
    }

    const delta = figure;

    if (row.figure.trim() === '' || !Number.isFinite(delta)) {
      problems.push(`Say how much to take in or let out on ${measure}.`);
      continue;
    }

    // A measurement that changes nothing is not a measurement, and the docket picks its verb off
    // the sign — a zero would print "Increase sleeve by 0 inches".
    if (delta === 0) {
      problems.push(`${measure} changes nothing. Give a figure, or remove the line.`);
      continue;
    }

    if (Math.abs(delta) > maxAlterationInches) {
      problems.push(
        `${measure} is ${delta} inches, past the ${maxAlterationInches}-inch limit. Check the figure.`,
      );
    }
  }

  return problems;
}

/**
 * Today as the date input writes it, in the browser's own timezone.
 *
 * The server compares against the UTC date, which is the rough edge `Today()` already carries. Ahead
 * of UTC — which India is — the local date is the later of the two, so this refuses a shade earlier
 * than the server does and never the other way round. That is the safe direction: the clerk is told
 * at the field rather than by a 400.
 */
export function todayForDateInput(): string {
  const now = new Date();
  const pad = (part: number) => String(part).padStart(2, '0');

  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * What stands between a garment and the tailor, beyond the measurements.
 *
 * <p>
 * <strong>The due date is required, and that is newer than it looks.</strong> It used to be a
 * convenience — a garment at the tailor with no due date is one nobody can chase — and is now
 * load-bearing: sending a garment out takes it off the rail from today until this date, so without
 * an end the block would either not exist, leaving a garment bookable while it is physically in a
 * workshop, or run forever.
 * </p>
 */
export function tailorProblems(dueOn: string, today: string = todayForDateInput()): string[] {
  if (dueOn.trim() === '') {
    return ['Say when the garment is due back. It comes off the rail until then.'];
  }

  // A window that closed yesterday holds nothing, so the garment would be at the tailor and on the
  // rail at the same time — which is the whole thing the block exists to prevent.
  if (dueOn < today) {
    return ['The due date has passed. Give the date the garment is actually expected back.'];
  }

  return [];
}

/**
 * The rows as the API takes them — blanks dropped, figures parsed, order preserved.
 *
 * Position is the whole of `SortOrder` on the server, so the order the clerk arranged is the order
 * the tailor reads.
 */
export function toInput(rows: readonly DraftAlteration[]): GarmentAlterationInput[] {
  return rows
    .filter((row) => !isBlank(row))
    .map((row) => ({
      measure: row.measure.trim(),
      ...(row.kind === 'size'
        ? { sizeInches: Number(row.figure) }
        : { deltaInches: Number(row.figure) }),
      note: row.note.trim() || null,
    }));
}

/**
 * The rows as the narrative renderer wants them, so the drawer can preview the tailor's own
 * sentence while it is being typed.
 *
 * Ids are fabricated because `AlterationDetail` is a response shape and nothing here has one yet;
 * `alterationNarrative` reads only the measure, the figures and the note.
 */
export function toPreview(rows: readonly DraftAlteration[]): AlterationDetail[] {
  return toInput(rows).map((row, index) => ({
    id: index,
    measure: row.measure,
    deltaInches: row.deltaInches ?? null,
    sizeInches: row.sizeInches ?? null,
    note: row.note ?? null,
  }));
}
